import { createReducer, on } from '@ngrx/store'
import {
  appendMessage,
  createConversation,
  deleteConversation,
  discardDraftIfEmpty,
  hydrateAssistantResult,
  loadConversationSuccess,
  loadConversationsSuccess,
  setConversationTitle,
  togglePinned,
  updateLastAssistantMessage,
} from '../actions/analytics-copilot.actions'
import {
  CopilotConversation,
  CopilotConversationDetail,
  CopilotConversationListItem,
  CopilotQueryStatus,
} from '../analytics/copilot/models/copilot.model'

export type AnalyticsCopilotConversationsState = Record<
  string,
  Record<string, CopilotConversation>
>

export const initialState: AnalyticsCopilotConversationsState = {}

function summaryToConversation(
  s: CopilotConversationListItem
): CopilotConversation {
  return {
    id: s.id,
    title: s.title || null,
    messages: [],
    isPinned: s.is_pinned,
    createdAt: new Date(s.created_at).getTime(),
    updatedAt: new Date(s.updated_at).getTime(),
  }
}

function detailToConversation(
  d: CopilotConversationDetail
): CopilotConversation {
  const messages: CopilotConversation['messages'] = []
  for (const m of d.messages) {
    messages.push({ role: 'user', content: m.content })
    if (m.status === CopilotQueryStatus.Completed) {
      messages.push({
        role: 'assistant',
        content: '',
        activity: m.activity ?? [],
        messageId: m.id,
      })
    }
  }
  return {
    id: d.id,
    title: d.title || null,
    messages,
    isPinned: d.is_pinned,
    createdAt: new Date(d.created_at).getTime(),
    updatedAt: new Date(d.updated_at).getTime(),
  }
}

export const reducer = createReducer(
  initialState,
  on(createConversation, (state, { userId, conversation }) => ({
    ...state,
    [userId]: {
      ...(state[userId] ?? {}),
      [conversation.id]: conversation,
    },
  })),
  on(appendMessage, (state, { userId, conversationId, message }) => {
    const userConvs = state[userId] ?? {}
    const conv = userConvs[conversationId]
    if (!conv) return state
    return {
      ...state,
      [userId]: {
        ...userConvs,
        [conversationId]: {
          ...conv,
          messages: [...conv.messages, message],
          updatedAt: Date.now(),
        },
      },
    }
  }),
  on(setConversationTitle, (state, { userId, conversationId, title }) => {
    const userConvs = state[userId] ?? {}
    const conv = userConvs[conversationId]
    if (!conv) return state
    return {
      ...state,
      [userId]: {
        ...userConvs,
        [conversationId]: { ...conv, title, updatedAt: Date.now() },
      },
    }
  }),
  on(updateLastAssistantMessage, (state, { userId, conversationId, patch }) => {
    const userConvs = state[userId] ?? {}
    const conv = userConvs[conversationId]
    if (!conv) return state
    const messages = [...conv.messages]
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        messages[i] = { ...messages[i], ...patch }
        break
      }
    }
    return {
      ...state,
      [userId]: {
        ...userConvs,
        [conversationId]: { ...conv, messages, updatedAt: Date.now() },
      },
    }
  }),
  on(togglePinned, (state, { userId, conversationId }) => {
    const userConvs = state[userId] ?? {}
    const conv = userConvs[conversationId]
    if (!conv) return state
    return {
      ...state,
      [userId]: {
        ...userConvs,
        [conversationId]: {
          ...conv,
          isPinned: !conv.isPinned,
          updatedAt: Date.now(),
        },
      },
    }
  }),
  on(deleteConversation, (state, { userId, conversationId }) => {
    const userConvs = { ...(state[userId] ?? {}) }
    delete userConvs[conversationId]
    return { ...state, [userId]: userConvs }
  }),
  on(discardDraftIfEmpty, (state, { userId, conversationId }) => {
    const userConvs = state[userId] ?? {}
    const conv = userConvs[conversationId]
    if (!conv || conv.messages.length > 0) return state
    const next = { ...userConvs }
    delete next[conversationId]
    return { ...state, [userId]: next }
  }),
  on(loadConversationsSuccess, (state, { userId, conversations }) => {
    const existing = state[userId] ?? {}
    const merged = { ...existing }
    for (const summary of conversations) {
      // Only seed if not already in store (in-progress conversations take precedence)
      if (!merged[summary.id]) {
        merged[summary.id] = summaryToConversation(summary)
      } else {
        // Update metadata but keep in-memory messages
        merged[summary.id] = {
          ...merged[summary.id],
          title: summary.title || null,
          isPinned: summary.is_pinned,
        }
      }
    }
    return { ...state, [userId]: merged }
  }),
  on(
    hydrateAssistantResult,
    (state, { userId, conversationId, messageId, result }) => {
      const userConvs = state[userId] ?? {}
      const conv = userConvs[conversationId]
      if (!conv) return state
      const messages = conv.messages.map(msg =>
        msg.messageId === messageId ? { ...msg, result } : msg
      )
      return {
        ...state,
        [userId]: { ...userConvs, [conversationId]: { ...conv, messages } },
      }
    }
  ),
  on(loadConversationSuccess, (state, { userId, conversation }) => {
    const userConvs = state[userId] ?? {}
    const existing = userConvs[conversation.id]
    // If conversation is currently active (has in-progress messages), keep those
    const loaded = detailToConversation(conversation)
    if (existing && existing.messages.length > loaded.messages.length) {
      return {
        ...state,
        [userId]: {
          ...userConvs,
          [conversation.id]: {
            ...existing,
            title: loaded.title,
            isPinned: loaded.isPinned,
          },
        },
      }
    }
    return {
      ...state,
      [userId]: {
        ...userConvs,
        [conversation.id]: loaded,
      },
    }
  })
)
